<template>
  <div class="ac-generator-3d">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>e = NBSω sin(ωt)$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 3D 渲染区 -->
      <div ref="canvasContainer" class="canvas-3d-wrapper">
         <div class="hud-top">
            <div class="phase-meter">
               <label>运行相位 ωt</label>
               <span>{{ ((angle % (Math.PI*2)) * 180 / Math.PI).toFixed(1) }}°</span>
            </div>
         </div>
         
         <div class="sine-overlay">
            <svg width="150" height="60" viewBox="0 0 150 60">
               <path :d="sinePath" fill="none" stroke="#2ed573" stroke-width="2" />
               <circle cx="130" :cy="30 - currentE * 25" r="3" fill="#ff4757" />
            </svg>
         </div>
      </div>

      <!-- 参数控制 -->
      <div class="bottom-controls">
         <div class="control-card glass">
            <div class="row">
               <div class="col">
                  <label>旋转角速度 (ω): {{ (speed * 10).toFixed(0) }} rad/s</label>
                  <el-slider v-model="speed" :min="0" :max="5" :step="0.1" size="mini"></el-slider>
               </div>
               <div class="col center">
                  <el-button type="primary" size="mini" @click="isRotating = !isRotating">
                     {{ isRotating ? '停止转动' : '开始转动' }}
                  </el-button>
               </div>
            </div>
         </div>

         <div class="law-card">
            <p><strong>原理说明：</strong>线圈在匀强磁场中绕垂直于磁场的轴匀速转动，穿过线圈的<strong>磁通量</strong>按正弦规律变化，从而产生正弦交流电。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'ACGeneratorProViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      speed: 1.5,
      angle: 0,
      isRotating: true,
      running: true
    }
  },
  computed: {
    currentE() { return Math.sin(this.angle); },
    sinePath() {
       let d = "M 0 30";
       for(let i=0; i<=130; i++) {
          const a = this.angle - (130-i) * 0.1;
          const y = 30 - Math.sin(a) * 25;
          d += ` L ${i} ${y}`;
       }
       return d;
    }
  },
  mounted() { this.initThree(); },
  beforeDestroy() { 
    this.running = false;
    if (this.renderer) {
      this.renderer.dispose();
      this.canvasContainer.removeChild(this.renderer.domElement);
    }
  },
  methods: {
    initThree() {
      this.canvasContainer = this.$refs.canvasContainer;
      const width = this.canvasContainer.clientWidth;
      const height = this.canvasContainer.clientHeight;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
      this.camera.position.set(50, 40, 60);
      this.camera.lookAt(0, 0, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.canvasContainer.appendChild(this.renderer.domElement);

      // Mag poles
      const poleGeo = new THREE.BoxGeometry(20, 40, 40);
      const nPole = new THREE.Mesh(poleGeo, new THREE.MeshPhongMaterial({ color: 0xff4757 }));
      nPole.position.x = -40;
      this.scene.add(nPole);
      const sPole = new THREE.Mesh(poleGeo, new THREE.MeshPhongMaterial({ color: 0x2f3542 }));
      sPole.position.x = 40;
      this.scene.add(sPole);

      // Wire Coil
      const coilGeo = new THREE.BoxGeometry(1, 40, 30);
      this.coil = new THREE.Mesh(coilGeo, new THREE.MeshPhongMaterial({ color: 0xffd700, wireframe: true }));
      this.scene.add(this.coil);

      // Shaft
      const shaft = new THREE.Mesh(new THREE.CylinderGeometry(1, 1, 60, 16), new THREE.MeshPhongMaterial({ color: 0x888 }));
      this.scene.add(shaft);

      this.scene.add(new THREE.AmbientLight(0xffffff, 0.5));
      const light = new THREE.DirectionalLight(0xffffff, 0.8);
      light.position.set(10, 50, 50);
      this.scene.add(light);

      this.animate();
    },
    animate() {
      if (!this.running) return;
      requestAnimationFrame(this.animate);
      
      if (this.isRotating) {
          this.angle += this.speed * 0.05;
          this.coil.rotation.y = this.angle;
      }

      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped lang="scss">
.ac-generator-3d {
  flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden;
}
.viz-header { display: flex; justify-content: space-between; align-items: center; .title { font-size: 1rem; color: #fff; } .formula-chip { color: #2ed573; background: rgba(46,213,115,0.1); padding: 4px 12px; border-radius: 8px; font-weight: bold; } }
.canvas-3d-wrapper {
  flex: 1.5; background: #000; border-radius: 12px; position: relative; overflow: hidden;
  .hud-top { position: absolute; top: 15px; left: 15px; .phase-meter { background: rgba(255,255,255,0.05); padding: 5px 12px; border-radius: 6px; border-left: 3px solid #ffd700; label { display: block; font-size: 0.55rem; color: #888; } span { font-size: 0.9rem; color: #fff; font-family: monospace; } } }
  .sine-overlay { position: absolute; bottom: 15px; right: 15px; background: rgba(0,0,0,0.6); padding: 5px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); }
}
.bottom-controls { flex: 0.5; display: flex; flex-direction: column; gap: 12px; }
.control-card { background: rgba(255,255,255,0.03); border-radius: 12px; padding: 15px; .row { display: grid; grid-template-columns: 1fr 100px; gap: 20px; align-items: center; } }
.law-card { background: rgba(46, 213, 115, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #888; line-height: 1.5; strong { color: #fff; } } }
</style>
